import React from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Container, Typography, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RuleRoundedIcon from "@mui/icons-material/RuleRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { FAQ_CONTENT } from "../content/faq";
import { MUTUALITEITEN_INFO, REIMB_VERIFIED_DATE, Lang } from "../content/mutualiteiten";
import Logo from "../components/Logo";

/** Homepage. Opbouw: hero met ademende deken-illustratie (het logomerk,
 *  verwijzend naar de in-app ademhalingsoefening) → propositie-strip →
 *  stappenrail → twee feature-rijen met échte inhoud als visual
 *  (ziekenfonds-bedragen uit content/mutualiteiten.ts en de werkelijke
 *  verificatiechecklist) → feitenblok → werkgeverspaneel (donker, warm) →
 *  FAQ-preview → slot-CTA. Alle claims eerlijk: geen verzonnen testimonials,
 *  bedragen indicatief mét verificatiedatum. */

/** De deken — het logomerk uitvergroot: amberen cirkel met gedrapeerde bogen
 *  in terracotta/pompoen/goud. Ademt traag (8s) als stille verwijzing naar de
 *  ademhalingsoefening; prefers-reduced-motion zet dit stil (index.css). */
function BlanketHero() {
  return (
    <Box
      aria-hidden
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        animation: "breathe 8s ease-in-out infinite",
        transformOrigin: "50% 65%",
      }}
    >
      <svg width="340" height="300" viewBox="0 0 260 230" style={{ maxWidth: "100%", height: "auto" }}>
        <circle cx="130" cy="150" r="52" fill="#E0A21C" opacity="0.92" />
        {/* Gedrapeerde bogen — zelfde gebaar als het logo, in drie lagen. */}
        <path d="M62 150 A 68 68 0 0 1 198 150" fill="none" stroke="#C8381E" strokeWidth="15" strokeLinecap="round" />
        <path d="M40 150 A 90 90 0 0 1 220 150" fill="none" stroke="#C8682E" strokeWidth="9" strokeLinecap="round" opacity="0.55" />
        <path d="M22 150 A 108 108 0 0 1 238 150" fill="none" stroke="#E0B85C" strokeWidth="6" strokeLinecap="round" opacity="0.4" />
      </svg>
    </Box>
  );
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = ((i18n.language || "nl").slice(0, 2) as Lang);

  const props = [
    { icon: <BoltRoundedIcon />, color: "#C8682E", title: t("landing.prop1Title"), body: t("landing.prop1Body") }, // pumpkin orange
    { icon: <VerifiedUserRoundedIcon />, color: "#3E7D4E", title: t("landing.prop2Title"), body: t("landing.prop2Body") }, // forest green
    { icon: <SavingsRoundedIcon />, color: "#C2891F", title: t("landing.prop3Title"), body: t("landing.prop3Body") }, // gold
  ];

  const steps = [1, 2, 3, 4].map((n) => ({ n, title: t(`howItWorks.step${n}Title`), body: t(`howItWorks.step${n}Body`) }));

  // Feitenblok: uitsluitend verifieerbare, elders op de site onderbouwde waarden.
  const facts = [
    { icon: <ScheduleRoundedIcon />, title: t("landing.fact1Title"), body: t("landing.fact1Body") },
    { icon: <ReceiptLongRoundedIcon />, title: t("landing.fact2Title"), body: t("landing.fact2Body") },
    { icon: <ShieldRoundedIcon />, title: t("landing.fact3Title"), body: t("landing.fact3Body") },
    { icon: <RuleRoundedIcon />, title: t("landing.fact4Title"), body: t("landing.fact4Body") },
  ];

  // Ziekenfonds-teaser: échte, indicatieve bedragen uit dezelfde bron als
  // /terugbetaling — mét verificatiedatum, nooit uit de lucht gegrepen.
  const funds = MUTUALITEITEN_INFO.slice(0, 4);
  const verifiedDate = REIMB_VERIFIED_DATE[lang] || REIMB_VERIFIED_DATE.nl;

  const vettingChecks = [1, 2, 3, 4].map((n) => t(`landing.vettingCheck${n}`));

  // FAQ-preview: eerste vragen uit de echte FAQ-inhoud, zodat homepage en
  // FAQ-pagina nooit uiteenlopen.
  const faq = FAQ_CONTENT[lang] || FAQ_CONTENT.nl;
  const faqPreview = [
    faq.groups[0]?.items[0], // hoe werkt het
    faq.groups[0]?.items[1], // hoe snel
    faq.groups[1]?.items[1], // terugbetaling
    faq.groups[2]?.items[1], // vertrouwelijkheid
  ].filter(Boolean) as { id: string; q: string; a: string[] }[];

  const sectionTitle = { fontSize: { xs: "1.75rem", sm: "2.25rem" }, mb: 1.5, letterSpacing: "-0.015em" } as const;
  // Actiekleur volgens docs/DESIGN.md: terracotta (wit op #C8381E ≈ 4.6:1, AA) —
  // het eerdere goud (#C2891F) haalde met witte tekst geen 4.5:1.
  const ctaSx = { textTransform: "none", bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" } } as const;
  // Stille sectielabels (eyebrow) — wegwijzer voor wie scant, in AA-goud.
  const eyebrow = { display: "block", fontSize: ".8rem", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--accent-700)", mb: 1.5 } as const;

  const fadeUp = (delay: number) => ({ animation: `fade-up .5s ease-out ${delay}ms both` });

  return (
    <Box sx={{ bgcolor: "background.default", overflow: "hidden" }}>
      {/* ── Hero: kop + CTA links, ademende deken rechts. ── */}
      <Container maxWidth="lg" sx={{ pt: { xs: 7, sm: 10 }, pb: { xs: 7, sm: 10 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" }, gap: { xs: 4, md: 8 }, alignItems: "center" }}>
          {/* Op smalle schermen (één kolom, geen illustratie) centreren we de
              hero en de strips — links uitgelijnd oogt daar onaf. */}
          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
            <Typography variant="h1" sx={{ fontSize: { xs: "2.5rem", sm: "3.25rem", lg: "3.75rem" }, mb: 3, maxWidth: "16em", mx: { xs: "auto", md: 0 }, ...fadeUp(0) }}>
              {t("landing.heroPre")}{" "}
              <Box
                component="span"
                sx={{
                  background: "var(--autumn-gradient)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "primary.main", // fallback if background-clip unsupported
                }}
              >
                {t("landing.heroEmphasis")}
              </Box>{" "}
              {t("landing.heroPost")}
            </Typography>
            <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem" }, maxWidth: 560, mx: { xs: "auto", md: 0 }, mb: 4, lineHeight: 1.65, ...fadeUp(80) }}>
              {t("landing.subtitle")}
            </Typography>
            <Box sx={fadeUp(160)}>
              {/* CTA-cluster: één primaire actie, met een vrijblijvende
                  zijdeur voor wie eerst wil begrijpen hoe het werkt. */}
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", justifyContent: { xs: "center", md: "flex-start" }, gap: { xs: 1.5, sm: 2.5 } }}>
                <Button variant="contained" size="large" onClick={() => navigate("/start")} sx={{ minWidth: 240, width: { xs: "100%", sm: "auto" }, py: 1.5, fontSize: "1.0625rem", ...ctaSx }}>
                  {t("landing.cta")}
                </Button>
                <Button component={RouterLink} to="/hoe-het-werkt" size="large" sx={{ textTransform: "none", fontSize: "1rem" }}>
                  {t("landing.ctaSecondary")}
                </Button>
              </Box>
              <Typography variant="caption" sx={{ display: "block", mt: 1.75, color: "text.secondary" }}>
                {t("landing.ctaCaption")}
              </Typography>
            </Box>
          </Box>

          {/* ── Hero-visual: de ademende deken in een warme gloed, met twee
              eerlijke ankers — de uitkomst (jouw voorstel, enkel controleerbare
              feiten) en het tempo (48u-claim uit het feitenblok). ── */}
          <Box sx={{ position: "relative", display: { xs: "none", md: "block" }, ...fadeUp(240) }}>
            {/* Warme gloed — hét accentmoment van deze pagina. */}
            <Box aria-hidden sx={{ position: "absolute", inset: "-14% -10%", borderRadius: "50%", background: "radial-gradient(closest-side, rgba(224,162,28,.20), rgba(224,162,28,0) 72%)" }} />
            <BlanketHero />
            <Box
              sx={{
                position: "absolute", top: 0, right: { md: -8, lg: 8 },
                display: "flex", alignItems: "center", gap: 1,
                bgcolor: "var(--surface)", borderRadius: "var(--radius-full)", boxShadow: "var(--shadow-md)",
                px: 2, py: 0.75,
              }}
            >
              <ScheduleRoundedIcon sx={{ fontSize: 18, color: "var(--orange-500)" }} />
              <Typography sx={{ m: 0, fontSize: ".85rem", fontWeight: 600, whiteSpace: "nowrap" }}>{t("landing.fact1Title")}</Typography>
            </Box>
            <Box
              sx={{
                position: "absolute", bottom: -20, left: { md: -12, lg: -4 },
                bgcolor: "var(--surface)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-lg)",
                p: 2.5, pr: 3,
                // Zachte zweefbeweging start pas ná de fade-up (zelfde
                // transform-kanaal); reduced-motion zet beide stil.
                animation: "float-soft 7s ease-in-out .9s infinite",
              }}
            >
              <Typography sx={{ m: 0, mb: 1, fontSize: ".78rem", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--accent-700)" }}>
                {t("landing.heroCardTitle")}
              </Typography>
              {[1, 2, 3].map((n) => (
                <Box key={n} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                  <CheckRoundedIcon sx={{ fontSize: 18, color: "var(--success)" }} />
                  <Typography sx={{ m: 0, fontSize: ".9rem", fontWeight: 500, whiteSpace: "nowrap" }}>{t(`landing.heroCardCheck${n}`)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Propositie-strip: geen kaartjes — ruimte en een fijne lijn doen het werk. */}
        <Box
          sx={{
            mt: { xs: 6, sm: 9 },
            pt: { xs: 4, sm: 5 },
            borderTop: "1px solid var(--border)",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: { xs: 3, sm: 5 },
            ...fadeUp(240),
          }}
        >
          {props.map((v) => (
            <Box key={v.title} sx={{ display: "flex", gap: 1.75, flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "center", sm: "flex-start" }, textAlign: { xs: "center", sm: "left" } }}>
              <Box sx={{ color: v.color, mt: 0.25, "& svg": { fontSize: 28 } }}>{v.icon}</Box>
              <Box>
                <Typography variant="h3" sx={{ fontSize: "1.1rem", mb: 0.25 }}>{v.title}</Typography>
                <Typography sx={{ m: 0, fontSize: ".95rem", color: "text.secondary" }}>{v.body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ── Hoe werkt het — stappenrail; detail op /hoe-het-werkt. ── */}
      <Box sx={{ bgcolor: "var(--surface-alt)" }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, sm: 11 } }}>
          <Typography variant="h2" sx={{ ...sectionTitle, textAlign: "center" }}>{t("landing.howTitle")}</Typography>
          <Typography sx={{ textAlign: "center", maxWidth: 560, mx: "auto", mb: 6, color: "text.secondary" }}>{t("landing.howSubtitle")}</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: { xs: 4, lg: 3 } }}>
            {steps.map((s, i) => (
              <Box key={s.n} sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: { xs: "center", sm: "flex-start" }, mb: 2 }}>
                  <Box
                    sx={{
                      width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                      bgcolor: "var(--surface)", border: "1px solid var(--primary-200)",
                      color: "var(--primary-700)", display: "grid", placeItems: "center",
                      fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 600,
                    }}
                  >
                    {s.n}
                  </Box>
                  {/* Groen = positief moment op de rail: de kennismaking is echt gratis. */}
                  {s.n === 3 && (
                    <Chip label={t("landing.stepFreeBadge")} size="small" sx={{ ml: 1.5, bgcolor: "var(--success)", color: "#fff", fontWeight: 600, fontSize: ".72rem", height: 22 }} />
                  )}
                  {/* Verbindingslijn van de rail — alleen zichtbaar als de vier
                      stappen op één rij staan. */}
                  {i < steps.length - 1 && (
                    <Box sx={{ display: { xs: "none", lg: "block" }, flexGrow: 1, height: "1px", bgcolor: "var(--neutral-300)", ml: 1.5, mr: -1.5 }} />
                  )}
                </Box>
                <Typography variant="h3" sx={{ fontSize: "1.1rem", mb: 0.5 }}>{s.title}</Typography>
                <Typography sx={{ m: 0, fontSize: ".95rem", color: "text.secondary", pr: { lg: 2 } }}>{s.body}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ textAlign: "center", mt: 5 }}>
            <Button component={RouterLink} to="/hoe-het-werkt" sx={{ textTransform: "none" }}>{t("landing.howLink")}</Button>
          </Box>
        </Container>
      </Box>

      {/* ── Terugbetaling: tekst + échte indicatieve bedragen per ziekenfonds. ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, sm: 11 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" }, gap: { xs: 4, md: 8 }, alignItems: "center" }}>
          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
            <Typography variant="caption" sx={eyebrow}>{t("landing.prop3Title")}</Typography>
            <Typography variant="h2" sx={sectionTitle}>{t("landing.reimbTitle")}</Typography>
            <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("landing.reimbBody1")}</Typography>
            <Typography sx={{ mb: 2.5, color: "text.secondary", lineHeight: 1.7 }}>{t("landing.reimbBody2")}</Typography>
            <Button component={RouterLink} to="/terugbetaling" sx={{ textTransform: "none", ml: { xs: 0, md: -1 } }}>{t("landing.reimbLink")}</Button>
          </Box>
          <Box sx={{ bgcolor: "var(--surface)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-md)", p: { xs: 3, sm: 4 } }}>
            <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 2 }}>{t("landing.reimbCardTitle")}</Typography>
            {funds.map((m, i) => (
              <Box key={m.code} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 1.25, borderTop: i > 0 ? "1px solid var(--border)" : "none" }}>
                <Typography sx={{ m: 0, fontWeight: 600, fontSize: ".95rem" }}>{m.name.split(" (")[0]}</Typography>
                <Typography sx={{ m: 0, fontSize: ".95rem", color: "text.secondary", textAlign: "right" }}>{(m.amount[lang] || m.amount.nl).split(" (")[0]}</Typography>
              </Box>
            ))}
            <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary", lineHeight: 1.5 }}>
              {t("reimb.verifiedNote", { date: verifiedDate })}
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* ── Kwaliteit: de werkelijke verificatiechecklist + tekst. ── */}
      <Container maxWidth="lg" sx={{ pb: { xs: 7, sm: 11 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" }, gap: { xs: 4, md: 8 }, alignItems: "center" }}>
          <Box sx={{ order: { xs: 2, md: 1 }, bgcolor: "var(--surface)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-md)", p: { xs: 3, sm: 4 } }}>
            <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 2 }}>{t("landing.vettingChecksTitle")}</Typography>
            {vettingChecks.map((c) => (
              <Box key={c} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", py: 1 }}>
                <CheckRoundedIcon sx={{ color: "var(--success)", fontSize: 22, mt: "1px" }} />
                <Typography sx={{ m: 0, fontSize: ".95rem" }}>{c}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ order: { xs: 1, md: 2 }, textAlign: { xs: "center", md: "left" } }}>
            <Typography variant="caption" sx={eyebrow}>{t("landing.prop2Title")}</Typography>
            <Typography variant="h2" sx={sectionTitle}>{t("landing.vettingTitle")}</Typography>
            <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("landing.vettingBody1")}</Typography>
            <Typography sx={{ mb: 2.5, color: "text.secondary", lineHeight: 1.7 }}>{t("landing.vettingBody2")}</Typography>
            <Button component={RouterLink} to="/kwaliteit-en-erkenning" sx={{ textTransform: "none", ml: { xs: 0, md: -1 } }}>{t("landing.vettingLink")}</Button>
          </Box>
        </Box>
      </Container>

      {/* ── Feitenblok — verifieerbare waarden i.p.v. verzonnen testimonials. ── */}
      <Box sx={{ bgcolor: "var(--surface-alt)" }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, sm: 11 } }}>
          <Typography variant="h2" sx={{ ...sectionTitle, textAlign: "center" }}>{t("landing.factsTitle")}</Typography>
          <Typography sx={{ textAlign: "center", maxWidth: 620, mx: "auto", mb: 6, color: "text.secondary" }}>{t("landing.factsSubtitle")}</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: { xs: 4, sm: 5 } }}>
            {facts.map((f) => (
              <Box key={f.title} sx={{ borderTop: "2px solid var(--accent-300)", pt: 2, textAlign: { xs: "center", sm: "left" } }}>
                <Box sx={{ color: "var(--accent-700)", mb: 1, "& svg": { fontSize: 26 } }}>{f.icon}</Box>
                <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 0.5 }}>{f.title}</Typography>
                <Typography sx={{ m: 0, fontSize: ".92rem", color: "text.secondary" }}>{f.body}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Werkgevers — het ene contrastmoment: donker, warm paneel. ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, sm: 11 } }}>
        <Box sx={{ bgcolor: "#2A231C", color: "#EDE6DA", borderRadius: "var(--radius-xl)", p: { xs: 4, sm: 8 }, textAlign: "center" }}>
          <Typography variant="h2" sx={{ ...sectionTitle, color: "#EDE6DA" }}>{t("landing.orgTitle")}</Typography>
          <Typography sx={{ maxWidth: 640, mx: "auto", mb: 3.5, color: "#B3A998", lineHeight: 1.7 }}>{t("landing.orgBody")}</Typography>
          <Button
            component={RouterLink}
            to="/voor-werkgevers"
            variant="outlined"
            sx={{ textTransform: "none", color: "#EDE6DA", borderColor: "#6E6253", "&:hover": { borderColor: "#EDE6DA", bgcolor: "rgba(237,230,218,0.06)" } }}
          >
            {t("landing.orgLink")}
          </Button>
        </Box>
      </Container>

      {/* ── FAQ-preview: titel links, vragen rechts. ── */}
      <Box sx={{ bgcolor: "var(--surface-alt)" }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, sm: 11 } }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "4fr 8fr" }, gap: { xs: 3, md: 8 } }}>
            <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
              <Typography variant="h2" sx={sectionTitle}>{t("landing.faqTitle")}</Typography>
              <Button component={RouterLink} to="/veelgestelde-vragen" sx={{ textTransform: "none", ml: { xs: 0, md: -1 } }}>{t("landing.faqLink")}</Button>
            </Box>
            <Box>
              {faqPreview.map((item) => (
                <Accordion key={item.id} disableGutters elevation={0}
                  sx={{ bgcolor: "transparent", borderBottom: "1px solid var(--border)", "&:before": { display: "none" }, "&.Mui-expanded": { m: 0 } }}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0, py: 0.75, "& .MuiAccordionSummary-content": { my: 1 } }}>
                    <Typography sx={{ fontWeight: 600, m: 0, color: "text.primary" }}>{item.q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0, pb: 2.5 }}>
                    {item.a.map((par, i) => (
                      <Typography key={i} sx={{ mb: i < item.a.length - 1 ? 1.5 : 0, color: "text.secondary", lineHeight: 1.7 }}>{par}</Typography>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Slot-CTA — het merk als rustpunt, één duidelijke actie. ── */}
      <Container maxWidth="md" sx={{ py: { xs: 9, sm: 13 }, textAlign: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Logo markOnly height={46} />
        </Box>
        <Typography variant="h2" sx={{ ...sectionTitle, fontSize: { xs: "1.9rem", sm: "2.5rem" }, mb: 3 }}>
          {t("landing.finalTitle")}
        </Typography>
        <Button variant="contained" size="large" onClick={() => navigate("/start")} sx={{ minWidth: 240, fontSize: "1.0625rem", ...ctaSx }}>
          {t("landing.cta")}
        </Button>
        <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "text.secondary" }}>
          {t("landing.ctaCaption")}
        </Typography>
      </Container>
    </Box>
  );
}
